// apps/office/src/pages/office/QuotationForm.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Trash2, Plus, Save, FileText, Package, X, Percent, DollarSign, Send } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';
import pb from '../../lib/pocketbaseClient';
import { generateQuoteNumber as genQuoteNum } from '../../lib/idGenerator';
import { useAuth } from '../../contexts/AuthContext';

const QuotationForm = () => {
  const { clientId, quoteId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const isEditing = !!quoteId;

  // State (Original + New)
  const [selectedJobId, setSelectedJobId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientJobIdText, setClientJobIdText] = useState('');
  const [clientDetails, setClientDetails] = useState({ name: '', phone: '', email: '', address: '', jobId: '' });
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [validUntil, setValidUntil] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  // ✨ Original global discount state (preserved)
  const [discountType, setDiscountType] = useState('percentage');
  const [discountValue, setDiscountValue] = useState(0);
  // Tax (original)
  const [taxRate, setTaxRate] = useState(18);
  // Package selector
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [selectedPackage, setSelectedPackage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showPackageSelector, setShowPackageSelector] = useState(false);

  // Queries (unchanged from original)
  const { data: existingQuote, isLoading: quoteLoading } = useQuery({
    queryKey: ['quotation', quoteId],
    queryFn: () => pb.collection('quotations').getOne(quoteId, { expand: 'clientJobId' }),
    enabled: !!quoteId,
  });

  const { data: specificLead, isLoading: leadLoading, error: leadError, isError: leadIsError } = useQuery({
    queryKey: ['lead', clientId],
    queryFn: () => pb.collection('leads').getOne(clientId),
    enabled: !!clientId,
    retry: 1,
  });

  const { data: leads = [], isLoading: leadsLoading } = useQuery({
    queryKey: ['leads', 'building-for-quotation'],
    queryFn: async () => {
      try {
        return await pb.collection('leads').getFullList({
          sort: '-created',
          filter: 'serviceType = "building"',
        });
      } catch (err) {
        console.error('[QuotationForm] Leads fetch error:', err);
        try {
          return await pb.collection('leads').getFullList({ sort: '-created' });
        } catch {
          return [];
        }
      }
    },
    enabled: !clientId && !isEditing,
    staleTime: 30000,
  });

  const { data: priceItems = [], isLoading: priceLoading } = useQuery({
    queryKey: ['priceList'],
    queryFn: () => pb.collection('price_list_items').getFullList({ sort: 'buildingType,name' }).catch(() => []),
  });

  // -------- Helpers --------
  const generateQuoteNumber = async (leadId, isNewVersion = false, previousNumber = null) => {
    return await genQuoteNum(leadId, isNewVersion, previousNumber);
  };

  // Load existing quote
  useEffect(() => {
    if (existingQuote) {
      setSelectedJobId(existingQuote.clientJobId || '');
      setClientJobIdText(existingQuote.clientJobIdText || existingQuote.expand?.clientJobId?.jobId || '');
      setClientDetails({
        name: existingQuote.clientName || '',
        phone: existingQuote.clientPhone || '',
        email: existingQuote.clientEmail || '',
        address: existingQuote.clientAddress || '',
        jobId: existingQuote.clientJobIdText || existingQuote.expand?.clientJobId?.jobId || '',
      });
      // ✨ Item with discount preserved
      setItems((existingQuote.items || []).map((it) => {
        const mrp = it.mrp || 0;
        const rate = it.rate || mrp;
        const discount = mrp > 0 ? Math.round(((mrp - rate) / mrp) * 100) : 0;
        return { ...it, mrp, rate, discount };
      }));
      setNotes(existingQuote.notes || '');
      setValidUntil(existingQuote.validUntil || '');
      setDiscountType(existingQuote.discountType || 'percentage');
      setDiscountValue(existingQuote.discountValue || 0);
      setTaxRate(existingQuote.taxRate || 18);
    }
  }, [existingQuote]);

  // Client from URL param
  useEffect(() => {
    if (clientId && specificLead) {
      setSelectedJobId(specificLead.id);
      setClientJobIdText(specificLead.jobId || '');
      setClientDetails({
        name: specificLead.name || '',
        phone: specificLead.phone || '',
        email: specificLead.email || '',
        address: specificLead.address || '',
        jobId: specificLead.jobId || '',
      });
    }
  }, [clientId, specificLead]);

  // Client from dropdown
  useEffect(() => {
    if (!clientId && !isEditing && selectedJobId) {
      const lead = leads.find((l) => l.id === selectedJobId);
      if (lead) {
        setClientJobIdText(lead.jobId || '');
        setClientDetails({
          name: lead.name || '',
          phone: lead.phone || '',
          email: lead.email || '',
          address: lead.address || '',
          jobId: lead.jobId || '',
        });
      }
    }
  }, [selectedJobId, leads, clientId, isEditing]);

  // Computed
  const filteredLeads = useMemo(() => {
    if (!clientSearch.trim()) return leads;
    const s = clientSearch.toLowerCase();
    return leads.filter(
      (l) =>
        l.name?.toLowerCase().includes(s) ||
        l.jobId?.toLowerCase().includes(s) ||
        l.phone?.toString().includes(s) ||
        l.email?.toLowerCase().includes(s)
    );
  }, [leads, clientSearch]);

  const buildingOptions = useMemo(
    () => ['All', ...new Set(priceItems.map((p) => p.buildingType).filter(Boolean))],
    [priceItems]
  );
  const packageOptions = useMemo(
    () => ['All', ...new Set(priceItems.map((p) => p.packageType).filter(Boolean))],
    [priceItems]
  );
  const filteredPackages = useMemo(() => {
    return priceItems.filter((pkg) => {
      const matchesBuilding = !selectedBuilding || selectedBuilding === 'All' || pkg.buildingType === selectedBuilding;
      const matchesPackage = !selectedPackage || selectedPackage === 'All' || pkg.packageType === selectedPackage;
      const matchesSearch = !searchTerm || pkg.name?.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesBuilding && matchesPackage && matchesSearch;
    });
  }, [priceItems, selectedBuilding, selectedPackage, searchTerm]);

  // -------- Item functions --------
  const addItemFromPackage = (pkg) => {
    const mrp = pkg.mrp || 0;
    const sellingPrice = pkg.sellingPrice || 0;
    const discountPct = mrp > 0 && sellingPrice > 0
      ? Math.round(((mrp - sellingPrice) / mrp) * 100)
      : 0;

    setItems([
      ...items,
      {
        id: Date.now(),
        priceItemId: pkg.id,
        name: pkg.name,
        description: pkg.details || '',
        unit: pkg.unit || 'Package',
        quantity: 1,
        mrp: mrp,
        discount: discountPct,
        rate: sellingPrice,
        amount: sellingPrice * 1,
      },
    ]);
    setShowPackageSelector(false);
    toast.success(`Added: ${pkg.name}`);
  };

  const recalcAmount = (item) => {
    const mrp = parseFloat(item.mrp) || 0;
    const disc = parseFloat(item.discount) || 0;
    const rate = mrp - (mrp * disc) / 100;
    const qty = parseFloat(item.quantity) || 0;
    return { ...item, rate, amount: rate * qty };
  };

  const updateItem = (index, field, value) => {
    setItems((prev) =>
      prev.map((it, i) => {
        if (i !== index) return it;
        let updated = { ...it, [field]: value };
        if (field === 'mrp' || field === 'discount' || field === 'quantity') {
          updated = recalcAmount(updated);
        }
        if (field === 'rate') {
          const mrp = parseFloat(updated.mrp) || 0;
          const rate = parseFloat(value) || 0;
          const disc = mrp > 0 ? Math.round(((mrp - rate) / mrp) * 100) : 0;
          updated.discount = disc;
          updated.amount = rate * (parseFloat(updated.quantity) || 0);
        }
        return updated;
      })
    );
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  // -------- Calculations (Original + New) --------
  const calculateSubtotal = () => items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);
  const calculateTotalItemDiscount = () => items.reduce((sum, i) => {
    const mrpTotal = (parseFloat(i.mrp) || 0) * (parseFloat(i.quantity) || 0);
    const discountAmt = (mrpTotal * (parseFloat(i.discount) || 0)) / 100;
    return sum + discountAmt;
  }, 0);

  // ✨ Original global discount calculation (preserved)
  const calculateGlobalDiscountAmount = () => {
    const subtotal = calculateSubtotal();
    if (discountType === 'percentage') return (subtotal * discountValue) / 100;
    return discountValue;
  };
  const calculateTaxAmount = () => {
    const afterDiscount = calculateSubtotal() - calculateGlobalDiscountAmount();
    return (afterDiscount * taxRate) / 100;
  };
  const calculateTotal = () => calculateSubtotal() - calculateGlobalDiscountAmount() + calculateTaxAmount();

  // Send for Approval mutation
  const sendForApproval = useMutation({
    mutationFn: async (id) => {
      const admins = await pb.collection('users').getFullList({
        filter: 'role = "admin" || role = "super_admin" || role = "manager"',
      });
      for (const admin of admins) {
        try {
          await pb.collection('notifications').create({
            userId: admin.id,
            type: 'quotation_approval',
            message: `${currentUser?.name} sent quotation for approval.`,
            quotationId: id,
            isRead: false,
          });
        } catch { /* ignore */ }
      }
      return pb.collection('quotations').update(id, {
        status: 'Pending Approval',
        approvalStatus: 'pending',
      });
    },
    onSuccess: () => {
      toast.success('✅ Sent for approval. Admins notified.');
      queryClient.invalidateQueries(['quotations']);
      navigate('/office/approvals');
    },
    onError: (err) => toast.error('Failed to send: ' + err.message),
  });

  // Submit
  const handleSubmit = async (action) => {
    if (!selectedJobId && !isEditing) {
      toast.error('Select a client first');
      return;
    }
    setIsSaving(true);
    try {
      let leadId = selectedJobId;
      if (isEditing && existingQuote) leadId = existingQuote.clientJobId;

      let quoteNumber;
      if (isEditing) {
        const baseNumber = existingQuote.quoteNumber.split('/')[0];
        quoteNumber = await generateQuoteNumber(leadId, true, baseNumber);
        await pb.collection('quotations').update(existingQuote.id, { isLatest: false });
      } else {
        quoteNumber = await (async () => {
          let attempts = 0;
          while (attempts < 10) {
            const candidate = await generateQuoteNumber(leadId);
            try {
              const dup = await pb.collection('quotations').getList(1, 1, { filter: `quoteNumber = "${candidate}"` });
              if (dup.items.length === 0) return candidate;
            } catch (e) { return candidate; }
            attempts++;
          }
          throw new Error('Could not generate unique quote number after 10 attempts');
        })();
      }

      const quoteData = {
        quoteNumber,
        clientJobId: leadId,
        clientName: clientDetails.name,
        clientEmail: clientDetails.email,
        clientPhone: clientDetails.phone,
        clientAddress: clientDetails.address,
        items: items.map((i) => ({
          itemId: i.priceItemId,
          name: i.name,
          description: i.description,
          unit: i.unit,
          quantity: i.quantity,
          rate: i.rate,
          mrp: i.mrp,
          discount: i.discount,
          amount: i.amount,
        })),
        subtotal: calculateSubtotal(),
        // ✨ BOTH discounts saved
        discountType,                                   // global
        discountValue,                                  // global
        discountAmount: calculateGlobalDiscountAmount(), // global
        totalItemDiscount: calculateTotalItemDiscount(), // per-item sum (new)
        taxRate,
        taxAmount: calculateTaxAmount(),
        total: calculateTotal(),
        validUntil: validUntil || null,
        status: action === 'send_approval' ? 'Pending Approval' : 'Draft',
        approvalStatus: action === 'send_approval' ? 'pending' : null,
        createdBy: currentUser?.id,
        notes,
        sentToClient: false,
        isLatest: true,
        previousVersionId: isEditing ? existingQuote?.id : null,
      };

      const jobIdForSave = clientJobIdText || clientDetails.jobId || '';
      if (jobIdForSave) quoteData.clientJobIdText = jobIdForSave;

      let record;
      if (isEditing) {
        record = await pb.collection('quotations').create(quoteData);
        toast.success(`✅ New version ${quoteNumber} created`);
      } else {
        record = await pb.collection('quotations').create(quoteData);
        toast.success(`✅ Quotation ${quoteNumber} saved`);
      }

      queryClient.invalidateQueries(['quotations']);

      if (action === 'send_approval') {
        await sendForApproval.mutateAsync(record.id);
        return;
      }
      navigate('/office/quotations');
    } catch (err) {
      console.error('Save error:', err);
      let msg = err.message || 'Failed to save';
      if (err.originalError?.data?.message) msg = err.originalError.data.message;
      if (err.originalError?.data?.data) {
        const fields = Object.entries(err.originalError.data.data)
          .map(([f, e]) => `${f}: ${e.message}`)
          .join('; ');
        if (fields) msg += ` (${fields})`;
      }
      toast.error('❌ ' + msg);
    } finally {
      setIsSaving(false);
    }
  };

  const formatPrice = (price) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(price || 0);

  const subtotal = calculateSubtotal();
  const globalDiscountAmount = calculateGlobalDiscountAmount();
  const totalItemDiscount = calculateTotalItemDiscount();
  const taxAmount = calculateTaxAmount();
  const total = calculateTotal();

  const leadsQueryEnabled = !clientId && !isEditing;
  if ((isEditing && quoteLoading) || (clientId && leadLoading) || (leadsQueryEnabled && leadsLoading)) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (clientId && leadIsError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-3 p-6">
        <div className="max-w-md bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-700 font-bold text-lg mb-2">Client not found</p>
          <p className="text-sm text-red-600 mb-4">{leadError?.message || 'The client ID is invalid.'}</p>
          <button onClick={() => navigate('/office/clients')} className="px-4 py-2 bg-red-600 text-white rounded-lg">
            Back to Clients
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-20">
      {/* Header */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 p-8 shadow-xl">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-white/10 rounded-full blur-2xl" />
        <div className="relative flex flex-col sm:flex-row justify-between items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white flex items-center gap-2">
              <FileText className="w-8 h-8" /> {isEditing ? 'Edit Quotation' : 'New Quotation'}
            </h1>
            <p className="text-white/80">{isEditing ? 'Create a new version' : 'Create professional quotation for client'}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => handleSubmit('save')} disabled={isSaving}
              className="bg-white text-indigo-600 hover:bg-indigo-50 shadow-lg gap-2">
              <Save className="w-4 h-4" /> {isSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button onClick={() => handleSubmit('send_approval')} disabled={isSaving} variant="outline"
              className="bg-white/20 text-white border-white/30 hover:bg-white/30 gap-2">
              <Send className="w-4 h-4" /> {isSaving ? 'Sending...' : 'Send for Approval'}
            </Button>
          </div>
        </div>
      </div>

      {/* Client Info (Original layout preserved) */}
      <Card className="border-0 shadow-xl overflow-visible">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
          <CardTitle className="flex items-center gap-2 text-indigo-800">Client Information</CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {!isEditing && !clientId ? (
              <div>
                <Label className="text-indigo-700 font-medium">Select Client</Label>
                <Select value={selectedJobId} onValueChange={setSelectedJobId}>
                  <SelectTrigger className="border-indigo-200"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent position="popper" className="max-h-[280px]">
                    <div className="sticky top-0 bg-white z-10 p-2 border-b">
                      <Input placeholder="Search client..." value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        onKeyDown={(e) => e.stopPropagation()} className="h-8 text-sm" autoFocus />
                    </div>
                    {filteredLeads.length === 0 ? (
                      <div className="p-3 text-center text-xs text-slate-400">No clients found</div>
                    ) : (
                      filteredLeads.map((lead) => (
                        <SelectItem key={lead.id} value={lead.id}>
                          <span className="font-mono text-xs text-indigo-600 mr-2">{lead.jobId || '—'}</span>
                          {lead.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-indigo-700 font-medium">Client</Label>
                <Input value={clientDetails.name || '—'} disabled className="bg-slate-50" />
              </div>
            )}
            <div>
              <Label className="text-indigo-700 font-medium">Job ID</Label>
              <Input value={clientDetails.jobId || '—'} disabled className="bg-slate-50 font-mono" />
            </div>
            <div>
              <Label className="text-indigo-700 font-medium">Mobile</Label>
              <Input value={clientDetails.phone || '—'} disabled className="bg-slate-50" />
            </div>
            <div>
              <Label className="text-indigo-700 font-medium">Email</Label>
              <Input value={clientDetails.email || '—'} disabled className="bg-slate-50" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-indigo-700 font-medium">Address</Label>
              <Input value={clientDetails.address || '—'} disabled className="bg-slate-50" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Items Table — Original columns + New columns */}
      <Card className="border-0 shadow-xl overflow-visible">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100 flex flex-row justify-between items-center">
          <CardTitle className="flex items-center gap-2 text-indigo-800">
            <Package className="w-5 h-5" /> Design Packages
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => setShowPackageSelector(true)}
            className="border-indigo-200 text-indigo-600 hover:bg-indigo-50">
            <Plus className="w-4 h-4 mr-1" /> Add Package
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="text-indigo-700 font-semibold">Package</TableHead>
                  <TableHead className="text-indigo-700 font-semibold">Description</TableHead>
                  <TableHead className="text-indigo-700 font-semibold">Unit</TableHead>
                  <TableHead className="text-indigo-700 font-semibold text-right">Qty</TableHead>
                  <TableHead className="text-indigo-700 font-semibold text-right">MRP (₹)</TableHead>
                  <TableHead className="text-indigo-700 font-semibold text-right">Discount %</TableHead>
                  <TableHead className="text-indigo-700 font-semibold text-right">Rate (₹)</TableHead>
                  <TableHead className="text-indigo-700 font-semibold text-right">Amount (₹)</TableHead>
                  <TableHead className="text-indigo-700 font-semibold text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item, idx) => (
                  <TableRow key={item.id} className="hover:bg-indigo-50/50">
                    <TableCell className="font-medium max-w-[180px]">
                      <div className="truncate">{item.name}</div>
                    </TableCell>
                    <TableCell className="max-w-[150px] truncate text-slate-500 text-xs">
                      {item.description || '—'}
                    </TableCell>
                    <TableCell className="text-sm text-slate-600">{item.unit}</TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                        className="w-16 text-right" min="1"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        value={item.mrp}
                        onChange={(e) => updateItem(idx, 'mrp', e.target.value)}
                        className="w-24 text-right" min="0"
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="relative inline-block">
                        <Input
                          type="number"
                          value={item.discount}
                          onChange={(e) => updateItem(idx, 'discount', e.target.value)}
                          className="w-20 text-right pr-6" min="0" max="100"
                        />
                        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Input
                        type="number"
                        value={Math.round(item.rate || 0)}
                        onChange={(e) => updateItem(idx, 'rate', e.target.value)}
                        className="w-24 text-right" min="0"
                      />
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatPrice(item.amount)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => removeItem(idx)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8 text-slate-500">
                      No packages added. Click "Add Package" to select packages.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Discount & Tax (Original Section — Full) */}
      {items.length > 0 && (
        <Card className="border-0 shadow-xl overflow-visible">
          <CardHeader className="bg-gradient-to-r from-amber-50 to-orange-50 border-b border-amber-200">
            <CardTitle className="flex items-center gap-2 text-amber-800">
              <Percent className="w-5 h-5" /> Discount & Tax
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Global Discount (Original) */}
              <div className="space-y-3">
                <Label className="text-amber-700 font-medium">Additional Discount</Label>
                <div className="flex gap-2">
                  <Button type="button" variant={discountType === 'percentage' ? 'default' : 'outline'}
                    size="sm" onClick={() => setDiscountType('percentage')} className="flex-1">
                    <Percent className="w-3 h-3 mr-1" /> %
                  </Button>
                  <Button type="button" variant={discountType === 'amount' ? 'default' : 'outline'}
                    size="sm" onClick={() => setDiscountType('amount')} className="flex-1">
                    <DollarSign className="w-3 h-3 mr-1" /> ₹
                  </Button>
                </div>
                <div className="flex gap-2 items-center">
                  <Input
                    type="number"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(parseFloat(e.target.value) || 0)}
                    placeholder={discountType === 'percentage' ? 'Discount %' : 'Discount Amount'}
                    className="flex-1"
                  />
                  {discountType === 'percentage' && <span className="text-slate-500">%</span>}
                </div>
                <p className="text-xs text-slate-500">
                  Applies on subtotal <b>after</b> per-item discounts
                </p>
              </div>

              {/* Tax (Original) */}
              <div className="space-y-3">
                <Label className="text-amber-700 font-medium">Tax (GST)</Label>
                <Select value={taxRate.toString()} onValueChange={(val) => setTaxRate(parseFloat(val))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[0, 5, 12, 18, 28].map((r) => (
                      <SelectItem key={r} value={r.toString()}>{r}%</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Totals Summary (Original + New) */}
              <div className="space-y-2 bg-slate-50 p-4 rounded-lg border border-slate-200">
                <div className="flex justify-between text-sm">
                  <span>Subtotal (after item disc.):</span>
                  <span className="font-medium">{formatPrice(subtotal)}</span>
                </div>
                {totalItemDiscount > 0 && (
                  <div className="flex justify-between text-xs text-emerald-600">
                    <span>Total Item Discount:</span>
                    <span>-{formatPrice(totalItemDiscount)}</span>
                  </div>
                )}
                {globalDiscountAmount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Additional Discount:</span>
                    <span>-{formatPrice(globalDiscountAmount)}</span>
                  </div>
                )}
                {taxAmount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span>GST ({taxRate}%):</span>
                    <span>+{formatPrice(taxAmount)}</span>
                  </div>
                )}
                <div className="border-t pt-2 mt-2">
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total:</span>
                    <span className="text-amber-600">{formatPrice(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Additional Details (Original) */}
      <Card className="border-0 shadow-xl overflow-visible">
        <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b border-indigo-100">
          <CardTitle className="flex items-center gap-2 text-indigo-800">
            <FileText className="w-5 h-5" /> Additional Details
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-4">
          <div>
            <Label className="text-indigo-700 font-medium">Valid Until</Label>
            <Input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
          </div>
          <div>
            <Label className="text-indigo-700 font-medium">Notes / Terms</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Payment terms, delivery timeline, warranty information, etc." />
          </div>
        </CardContent>
      </Card>

      {/* Package Selector Modal (Original) */}
      {showPackageSelector && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowPackageSelector(false)}>
          <div className="bg-white rounded-2xl max-w-4xl w-full flex flex-col max-h-[85vh] shadow-xl"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-xl font-bold flex items-center gap-2">
                <Package className="w-5 h-5" /> Select Design Package
              </h2>
              <button onClick={() => setShowPackageSelector(false)} className="text-slate-500 hover:text-slate-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 border-b bg-slate-50">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm">Search</Label>
                  <Input placeholder="Search packages..." value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)} />
                </div>
                <div>
                  <Label className="text-sm">Building Type</Label>
                  <Select value={selectedBuilding} onValueChange={setSelectedBuilding}>
                    <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      {buildingOptions.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm">Package Type</Label>
                  <Select value={selectedPackage} onValueChange={setSelectedPackage}>
                    <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
                    <SelectContent>
                      {packageOptions.map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              {priceLoading ? (
                <div className="text-center py-10 text-slate-500">Loading packages...</div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredPackages.map((pkg) => (
                    <div key={pkg.id} className="border rounded-xl p-4 hover:shadow-lg transition cursor-pointer"
                      onClick={() => addItemFromPackage(pkg)}>
                      <div className="flex gap-4">
                        {pkg.image && (
                          <img src={pb.files.getURL(pkg, pkg.image)} alt={pkg.name}
                            className="w-20 h-20 object-cover rounded-lg" />
                        )}
                        <div className="flex-1">
                          <h3 className="font-bold text-lg">{pkg.name}</h3>
                          <p className="text-sm text-slate-500">{pkg.buildingType} • {pkg.packageType}</p>
                          <div className="flex items-baseline gap-2 mt-1">
                            <span className="text-amber-600 font-semibold">{formatPrice(pkg.sellingPrice)}</span>
                            {pkg.mrp > pkg.sellingPrice && (
                              <span className="text-xs text-slate-400 line-through">{formatPrice(pkg.mrp)}</span>
                            )}
                          </div>
                          {pkg.details && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{pkg.details}</p>}
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredPackages.length === 0 && (
                    <div className="col-span-2 text-center py-10 text-slate-500">
                      No packages match your filters.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationForm;
